/**
 * faceGraphReachability — which face-graph nodes actually reach Final Pose.
 *
 * GraphEvaluator evaluates the face graph by walking BACKWARDS from `final`
 * along anim edges, so a node with no path to `final` contributes nothing.
 * "Plugged" for the Face Tracker (ENG-1107) means exactly that path exists —
 * through any number of Blend / Blend-by-Bool / Select nodes, at ANY weight
 * (a blend faded to 0 is still plugged: the user has not unwired anything).
 * Pure; the watcher in faceTrackState calls it at UI cadence, never per frame.
 *
 * The sibling of execReachability.js (the event graph's exec-chain rule that
 * arms/disarms the head-tracked camera), for the anim graph.
 */
export function reachableFromFinal(nodes, edges) {
  const seen = new Set()
  if (!Array.isArray(nodes) || !Array.isArray(edges)) return seen
  if (!nodes.some((n) => n?.id === 'final')) return seen
  const byTarget = new Map()
  for (const e of edges) {
    if (!e?.source || !e?.target) continue
    let list = byTarget.get(e.target)
    if (!list) byTarget.set(e.target, (list = []))
    list.push(e.source)
  }
  const stack = ['final']
  while (stack.length) {
    const id = stack.pop()
    if (seen.has(id)) continue
    seen.add(id)
    const srcs = byTarget.get(id)
    if (srcs) for (const s of srcs) if (!seen.has(s)) stack.push(s)
  }
  return seen
}

/** The first node of `type` with a path to `final`, else null. */
export function pluggedNodeOfType(type, nodes, edges) {
  const r = reachableFromFinal(nodes, edges)
  if (r.size === 0) return null
  for (const n of nodes) if (n?.type === type && r.has(n.id)) return n
  return null
}
